import { CommonModule, NgOptimizedImage } from '@angular/common';
import { Component, DestroyRef, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { Router } from '@angular/router';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';

import { VideoPlayerComponent } from '../video-player/video-player.component';
import { Question } from '../../services/models/question.model';
import { QuestionDataService } from '../../services/question-data.service';
import { DbStorageService } from '../../services/db-storage.service';
import { SharedService } from '../../services/shared.service';
import { LoggingService } from '../../services/logging.service';
import { fadeInOnEnterAnimation } from '../../animations/current-fade-in';

@Component({
  selector: 'app-main-page',
  standalone: true,
  imports: [
    CommonModule,
    VideoPlayerComponent,
    MatDividerModule,
    NgOptimizedImage,
    MatIconModule,
    MatButtonModule,
    MatTooltipModule,
  ],
  templateUrl: './main-page.component.html',
  styleUrls: ['./main-page.component.scss'],
  animations: [fadeInOnEnterAnimation()],
})
export class MainPageComponent {
  // Dependency injection using inject function
  private questionDataService = inject(QuestionDataService);
  private logger = inject(LoggingService);
  private router = inject(Router);
  private sharedService = inject(SharedService);
  private dbStorage = inject(DbStorageService);
  private breakpointObserver = inject(BreakpointObserver);
  private destroyRef = inject(DestroyRef);

  // Reactive state using signals
  readonly isMobile = signal(false);
  readonly hasSavedProgress = signal(false);
  readonly lastSavedTime = signal(0);
  readonly questions = signal<Question[]>([]);
  
  // Component properties
  public backgroundColor = '#f0f0f0';

  constructor() {
    this.initializeComponent();
  }

  /**
   * Initialize the component
   */
  private initializeComponent(): void {
    this.logPageView();
    this.loadQuestions();
    this.setupBreakpointObserver();
    this.loadSavedProgress();
    this.setupVideoCompletionListener();
    this.setupVideoPlayingListener();
  }

  /**
   * Format seconds into MM:SS string
   * @param seconds Number of seconds to format
   * @returns Formatted time string (MM:SS)
   */
  formatTime(seconds: number): string {
    const minutes = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${minutes}:${secs < 10 ? '0' : ''}${secs}`;
  }

  /**
   * Resume video from saved position
   */
  resumeFromSavedTime(): void {
    this.hasSavedProgress.set(false);
    this.sharedService.setResumeVideo(this.lastSavedTime());
  }

  /**
   * Save current time position
   * @param time Current time in seconds
   */
  saveCurrentTime(time: number): void {
    this.lastSavedTime.set(time);
  }

  /**
   * Start video from the beginning
   */
  startNewVideo(): void {
    this.lastSavedTime.set(0);
    this.hasSavedProgress.set(false);
    this.resetProgress();
  }

  /**
   * Log page view for analytics
   */
  private logPageView(): void {
    this.logger.logPageView('AHA Interactive Video Main Page');
  }

  /**
   * Load questions from the data service
   */
  private loadQuestions(): void {
    this.questions.set(this.questionDataService.getQuestions());
  }

  /**
   * Setup responsive layout detection
   */
  private setupBreakpointObserver(): void {
    this.breakpointObserver
      .observe([
        Breakpoints.XSmall,
        Breakpoints.Handset,
        Breakpoints.Small,
        Breakpoints.Medium,
        Breakpoints.Large,
        Breakpoints.XLarge,
      ])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(result => {
        this.updateLayoutBasedOnBreakpoint(result.breakpoints);
      });
  }

  /**
   * Update layout based on current breakpoint
   * @param breakpoints The current active breakpoints
   */
  private updateLayoutBasedOnBreakpoint(breakpoints: Record<string, boolean>): void {
    // Mobile view for small screens and handsets
    const isSmallScreen = 
      breakpoints[Breakpoints.XSmall] || 
      breakpoints[Breakpoints.Handset] || 
      breakpoints[Breakpoints.Small];
      
    // Update the isMobile signal
    this.isMobile.set(isSmallScreen);
  }

  /**
   * Load saved progress from storage
   */
  private loadSavedProgress(): void {
    this.dbStorage.getVideoProgress('dsd').then(time => {
      if (time) {
        this.lastSavedTime.set(time);
        this.hasSavedProgress.set(true);
      }
    });
  }

  /**
   * Setup listener for video completion
   */
  private setupVideoCompletionListener(): void {
    this.sharedService.videoCompleted$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(completed => {
        if (completed) {
          this.router.navigate(['/success']);
        }
      });
  }

  /**
   * Setup listener for video playing state
   */
  private setupVideoPlayingListener(): void {
    this.sharedService.playingVideo$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(playing => {
        if (playing) {
          this.hasSavedProgress.set(false);
        }
      });
  }

  /**
   * Reset all progress and start fresh
   */
  private resetProgress(): void {
    this.dbStorage.clearVideoProgress();
    this.sharedService.clearSavedProgressTime();
    this.sharedService.clearAnsweredQuestions();
    this.sharedService.setReplayVideo(true);
  }
}